import { useEffect, useState } from 'react';
import axios from 'axios';
import { Activity, CheckCircle2, AlertCircle, ExternalLink, RefreshCw, TestTube2 } from 'lucide-react';

const API = `${window.location.origin}/api`;

/**
 * Meta Conversions API health widget for Admin → Settings.
 *
 * Displays the state of the server-side CAPI pipeline in one glance:
 * config presence, total events sent, event-name breakdown, last success
 * timestamp, and how many of the recent orders had their Purchase event
 * actually forwarded to Meta (rough proxy for dedup / attribution
 * coverage). Also links out to Meta Events Manager for the authoritative
 * dashboard (EMQ + dedup rate live there).
 */
export default function CapiHealthCard({ getAuthHeaders }) {
  const [state, setState] = useState({ loading: true, data: null, err: null });

  const load = async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const r = await axios.get(`${API}/meta/status`, { headers: getAuthHeaders() });
      setState({ loading: false, data: r.data, err: null });
    } catch (e) {
      setState({ loading: false, data: null, err: e?.response?.data?.detail || e.message });
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  if (state.loading) {
    return (
      <div className="bg-[#141414] border border-white/5 p-4 sm:p-6" data-testid="capi-health-card">
        <div className="flex items-center gap-2 mb-1">
          <Activity className="w-4 h-4 text-[#D4AF37]" />
          <p className="text-white text-sm font-sans font-medium">Meta Conversions API Health</p>
        </div>
        <p className="text-white/40 text-xs font-sans mt-3">Checking Meta pipeline…</p>
      </div>
    );
  }

  if (state.err || !state.data) {
    return (
      <div className="bg-[#141414] border border-red-400/30 p-4 sm:p-6" data-testid="capi-health-card">
        <div className="flex items-center gap-2 mb-1">
          <AlertCircle className="w-4 h-4 text-red-400" />
          <p className="text-white text-sm font-sans font-medium">Meta CAPI Unreachable</p>
        </div>
        <p className="text-red-400/80 text-xs font-sans mt-2">{state.err || 'Unknown error'}</p>
      </div>
    );
  }

  const { config, counters, order_stats } = state.data;
  const healthy = config?.access_token_present && !!config?.dataset_id;
  const testing = !!config?.test_mode;
  const eventCounts = counters?.event_counts || {};
  const eventList = Object.entries(eventCounts).sort((a, b) => b[1] - a[1]);
  const relative = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    const diff = Math.max(0, Date.now() - d.getTime());
    const min = Math.floor(diff / 60000);
    if (min < 1) return 'just now';
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    return `${Math.floor(hr / 24)}d ago`;
  };

  return (
    <div className="bg-[#141414] border border-white/5 p-4 sm:p-6" data-testid="capi-health-card">
      <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-[#D4AF37]" />
          <p className="text-white text-sm font-sans font-medium">Meta Conversions API Health</p>
          {healthy ? (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] font-sans text-green-400 border border-green-400/40 px-2 py-0.5">
              <CheckCircle2 className="w-3 h-3" /> Live
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] font-sans text-red-400 border border-red-400/40 px-2 py-0.5">
              <AlertCircle className="w-3 h-3" /> Not configured
            </span>
          )}
          {testing && (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] font-sans text-[#D4AF37] border border-[#D4AF37]/40 px-2 py-0.5" title="Events are being sent with a test_event_code — remove META_TEST_EVENT_CODE from backend/.env before going live.">
              <TestTube2 className="w-3 h-3" /> Test mode
            </span>
          )}
        </div>
        <button
          onClick={load}
          className="text-white/50 hover:text-white text-xs flex items-center gap-1 transition-colors"
          data-testid="capi-refresh-btn"
        >
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {testing && (
        <p className="text-[#D4AF37]/80 text-xs font-sans mt-2 mb-3 border border-[#D4AF37]/30 bg-[#D4AF37]/5 px-3 py-2">
          <strong>Test mode active</strong> — events go to the "Test Events" tab in Meta Events Manager, NOT your live dataset. Remove <code>META_TEST_EVENT_CODE</code> from <code>backend/.env</code> to send events to production.
        </p>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 items-start">
        <Stat label="Events sent" value={counters?.total_sent ?? 0} hint={counters?.total_failed ? `${counters.total_failed} failed` : null} testid="capi-total-sent" />
        <Stat label="Last event" value={relative(counters?.last_success_at || counters?.last_event_at)} hint={counters?.last_event_name} testid="capi-last-event" />
        <Stat label="Purchase %" value={order_stats?.purchase_coverage_pct == null ? '—' : `${order_stats.purchase_coverage_pct}%`} hint={`${order_stats?.purchase_capi_sent || 0}/${order_stats?.recent_orders_checked || 0} orders`} testid="capi-purchase-coverage" />
        <Stat label="Match hint" value={order_stats?.match_quality_hint_pct == null ? '—' : `${order_stats.match_quality_hint_pct}%`} hint="With _fbp cookie" testid="capi-match-quality" />
      </div>

      {eventList.length > 0 && (
        <div className="mt-4">
          <p className="text-white/40 text-[10px] uppercase tracking-[0.2em] font-sans mb-2">Event breakdown</p>
          <div className="flex flex-wrap gap-1.5">
            {eventList.map(([name, n]) => (
              <span key={name} className="text-[11px] font-mono bg-white/5 border border-white/10 text-white/70 px-2 py-1" data-testid={`capi-event-${name}`}>
                {name} · <span className="text-white">{n}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {counters?.last_error_at && (
        <div className="mt-4 border border-red-400/30 bg-red-500/5 px-3 py-2">
          <p className="text-red-400 text-xs font-sans">
            <strong>Last error {relative(counters.last_error_at)}:</strong> {counters.last_error_status || ''} {counters.last_error_message?.slice(0, 200) || ''}
          </p>
        </div>
      )}

      <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/5 flex-wrap gap-2">
        <p className="text-white/40 text-[11px] font-sans">
          Dataset: <span className="text-white/60 font-mono">{config?.dataset_id || '—'}</span>
          {counters?.last_fbtrace_id && (
            <> · Last trace: <span className="text-white/40 font-mono text-[10px]">{counters.last_fbtrace_id.slice(0, 16)}…</span></>
          )}
        </p>
        <a
          href={`https://business.facebook.com/events_manager2/list/dataset/${config?.dataset_id || ''}/test_events`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#D4AF37] hover:text-[#F3E5AB] text-xs flex items-center gap-1 transition-colors"
          data-testid="capi-open-events-manager"
        >
          Open Events Manager <ExternalLink className="w-3 h-3" />
        </a>
      </div>
    </div>
  );
}

function Stat({ label, value, hint, testid }) {
  return (
    <div>
      <p className="text-white/50 text-[10px] uppercase tracking-[0.2em] font-sans mb-1 whitespace-nowrap">{label}</p>
      <p className="text-white text-lg font-serif" data-testid={testid}>{value}</p>
      {hint && <p className="text-white/50 text-[10px] font-sans mt-0.5">{hint}</p>}
    </div>
  );
}
